// 色（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { txt, txtHtml } from './text.js';
import { catByKey, tallUI } from './store.js';
import { $, keyOf } from './util.js';
import { groupOf } from './colorlink.js';
import { buildColors } from './lr.js';
import { histCut } from './history.js';
import type { Category } from './types.js';
import { setGrad } from './core/ops.js';
export function hexOf(rgb: string): string { return '#' + rgb.match(/\d+/g)!.map((v) => ('0' + Number(v).toString(16)).substr(-2)).join(''); }
export function setupGrad(cat: Category) {
  if (!(cat.GRAD.length > 1) || !C.state[keyOf(cat)]) { fold('gradFold', false); S.grad = null; drawGradBar(); return; }
  const st = C.state[keyOf(cat)];
  S.grad = { key: keyOf(cat), from: (st.from || cat.GRAD[1]).toLowerCase(), to: (st.to || cat.GRAD[0]).toLowerCase(), sel: { from: true, to: false } };
  fold('gradFold', true);
  drawGrad();
}
export const GRAD_PAIRS = [
  ['#f3d58a', '#fff6dc'], ['#5b3a29', '#b07a4f'], ['#1d1d24', '#3b4a8a'], ['#f28ab2', '#ffe3ee'], ['#6cc3e8', '#e6f7ff'],
  ['#8e6ad8', '#f2a7d0'], ['#c8323c', '#f59a4a'], ['#3fa36b', '#e6e27a'], ['#c9cfd8', '#7f9ccf'], ['#23191c', '#b8323f']
];
export function drawGrad() {
  if (!S.grad) return;
  const box = $('gradRow');
  box.textContent = '';
  const seg = document.createElement('div');
  seg.className = 'lr grad-seg';
  seg.setAttribute('role', 'radiogroup');
  seg.setAttribute('aria-label', txt('grad_where'));
  ([['from', 'grad_from'], ['to', 'grad_to']] as const).forEach((k) => {
    const on = !!S.grad!.sel[k[0]];
    const b = document.createElement('button');
    b.type = 'button';
    b.className = on ? 'on' : '';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', on ? 'true' : 'false');
    const dot = document.createElement('i');
    dot.style.background = S.grad![k[0]];
    b.appendChild(dot);
    b.appendChild(document.createTextNode(txt(k[1])));
    b.addEventListener('click', () => { S.grad!.sel = { from: k[0] === 'from', to: k[0] === 'to' }; drawGrad(); buildColors(catByKey[S.grad!.key]); });
    seg.appendChild(b);
  });
  seg.appendChild($<HTMLTemplateElement>('gradSwap').content.firstElementChild!.cloneNode(true));
  seg.lastChild!.addEventListener('click', swapGrad);
  box.appendChild(seg);
  const pairs = document.createElement('div');
  pairs.className = 'grad-pairs';
  pairs.setAttribute('aria-label', txt('grad_pairs'));
  const label = document.createElement('span');
  label.className = 'grad-pairs-label';
  label.textContent = txt('grad_pairs');
  pairs.appendChild(label);
  GRAD_PAIRS.forEach((p) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sw pair' + (S.grad!.from === p[0] && S.grad!.to === p[1] ? ' sel' : '');
    b.style.background = 'linear-gradient(180deg, ' + p[0] + ' 30%, ' + p[1] + ' 70%)';
    b.title = txt('grad_pair_title');
    b.setAttribute('aria-label', txt('grad_pair_title'));
    b.addEventListener('click', () => { pickPair(p); });
    pairs.appendChild(b);
  });
  box.appendChild(pairs);
  drawGradBar();
}
// おすすめの2色を付ける
function pickPair(p: string[]) {
  histCut();
  S.grad!.from = p[0]; S.grad!.to = p[1];
  applyGrad();
  buildColors(catByKey[S.grad!.key]);
}
// ---- 全身のメーカーのスマホ（tallUI）: グラデーションは色の列の右の縦のバー（2026-10-23、ユーザー判断） ----
// 上と下のつまみ（今の色の丸。押した方に枠）、そのあいだに今のグラデーション、上下の入れかえ、おすすめの2色（押すと小さな窓で出す）。
// つまみを押してから色の列の色を押すと、その側の色が変わる（上の段の「上の色・下の色」と同じ S.grad.sel）。縦なので「上」「下」の文字は付けない
let pairsOpen = false;
export function drawGradBar() {
  if (!tallUI) return;
  const bar = $('gradBar'), g = S.grad;
  bar.textContent = '';
  bar.hidden = !g;
  if (!g) { pairsOpen = false; return; }
  const handle = (k: 'from' | 'to', label: string) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gbar-h' + (g.sel[k] ? ' on' : '');
    b.style.background = g[k];
    b.title = txt(label);
    b.setAttribute('aria-label', txt(label));
    b.setAttribute('aria-pressed', g.sel[k] ? 'true' : 'false');
    b.addEventListener('click', () => { g.sel = { from: k === 'from', to: k === 'to' }; drawGrad(); buildColors(catByKey[g.key]); });
    return b;
  };
  bar.appendChild(handle('from', 'grad_from'));
  const line = document.createElement('div');
  line.className = 'gbar-line';
  line.style.background = 'linear-gradient(' + g.from + ', ' + g.to + ')';
  bar.appendChild(line);
  bar.appendChild(handle('to', 'grad_to'));
  const swap = $<HTMLTemplateElement>('gradSwap').content.firstElementChild!.cloneNode(true) as HTMLElement;
  swap.classList.add('gbar-swap');
  swap.addEventListener('click', swapGrad);
  bar.appendChild(swap);
  const rec = document.createElement('button');
  rec.type = 'button';
  rec.className = 'gbar-rec' + (pairsOpen ? ' on' : '');
  rec.title = txt('grad_pairs');
  rec.setAttribute('aria-expanded', pairsOpen ? 'true' : 'false');
  rec.innerHTML = '<i></i><span>' + txtHtml('grad_pairs_short') + '</span>';
  rec.addEventListener('click', () => { pairsOpen = !pairsOpen; drawGradBar(); });
  bar.appendChild(rec);
  if (!pairsOpen) return;
  const pop = document.createElement('div');
  pop.className = 'gbar-pairs';
  pop.innerHTML = '<p>' + txtHtml('grad_pairs') + '</p>';
  const grid = document.createElement('div');
  GRAD_PAIRS.forEach((p) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sw pair' + (g.from === p[0] && g.to === p[1] ? ' sel' : '');
    b.style.background = 'linear-gradient(180deg, ' + p[0] + ' 30%, ' + p[1] + ' 70%)';
    b.title = txt('grad_pair_title');
    b.setAttribute('aria-label', txt('grad_pair_title'));
    b.addEventListener('click', () => { pickPair(p); });
    grid.appendChild(b);
  });
  pop.appendChild(grid);
  bar.appendChild(pop);
}
// おすすめの2色の窓は、ほかの所を押したら閉じる
if (tallUI) {
  document.addEventListener('pointerdown', (e) => {
    if (pairsOpen && !(e.target as Element).closest('#gradBar')) { pairsOpen = false; drawGradBar(); }
  }, true);
}
// 上・下の色をグループのカテゴリに付ける（chibi.setAllGradColor と同じ）
export function applyGrad() {
  const cat = catByKey[S.grad!.key];
  setGrad(groupOf(cat), S.grad!.from, S.grad!.to);
  drawGrad();
}
export function swapGrad() {
  if (!S.grad) return;
  histCut(); // 元に戻すの区切り（前は区切らず、直前の色と1回分になっていた）
  const t = S.grad.from; S.grad.from = S.grad.to; S.grad.to = t;
  applyGrad();
  buildColors(catByKey[S.grad.key]);
}
// 段の出し入れ（閉じている段の中のボタンは押せない・選べないように inert）
export function fold(id: string, open: boolean) {
  const box = $(id);
  box.classList.toggle('open', open);
  box.inert = !open;
}
